// displayName.ts — demo-only identity alias map.
//
// The underlying Linux user on ws-david is `david`, but the corporate
// identity for the demo is `donwukwe` / "David Onwukwe". This file renders
// the friendly name in the UI without changing any backend data (sandbox
// lifecycle keys off the Linux username, so renaming there breaks
// reconciliation).
//
// To remove the alias once SSO/identity normalization lands properly,
// delete the entries and call sites will pass through unchanged.

const ALIASES: Record<string, string> = {
  david: "donwukwe",
};

// FULL_NAMES is keyed by BOTH the legacy unix username AND the canonical
// email — since the connector landed, correlation rows arrive keyed on the
// credential-bound owner_email (donwukwe@bluerock.io), not the unix user.
const FULL_NAMES: Record<string, string> = {
  david: "David Onwukwe",
  donwukwe: "David Onwukwe",
  "donwukwe@bluerock.io": "David Onwukwe",
};

// EMAIL_OVERRIDES — UI fallback when the probe didn't (or couldn't) populate
// payload.identity.email. Keyed by username. Same pattern as FULL_NAMES.
const EMAIL_OVERRIDES: Record<string, string> = {
  david: "donwukwe@bluerock.io",
  donwukwe: "donwukwe@bluerock.io",
};

function isEmail(s: string | undefined | null): s is string {
  return !!s && s.includes("@") && !s.includes(" ");
}

// titleizeLocalPart turns "jane.doe"/"jane_doe"/"jane-doe" → "Jane Doe".
// Best-effort friendly name when an identity arrives as a bare email with
// no entry in FULL_NAMES.
function titleizeLocalPart(email: string): string {
  return email
    .split("@")[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// IP_MASK — David enrolled the connector from his real laptop, so his recent
// reports carry his real home/office IP. For demos we never want that on
// screen; map it to a documentation-range (RFC 5737 TEST-NET) address, the
// same family the seed personas use. Keyed by the real IP. Demo-only, same
// spirit as the name aliases above — delete the entry to show real IPs.
const IP_MASK: Record<string, string> = {
  "68.129.23.120": "198.51.100.42",
};

export function displayExternalIp(ip: string | undefined | null): string {
  if (!ip) return "";
  return IP_MASK[ip] ?? ip;
}

// isPrivateOrDocIp — true for addresses that are NOT real public IPs: RFC 1918
// / link-local / loopback / ULA, plus the documentation ranges (RFC 5737 v4,
// RFC 3849 v6) the demo already uses. These are safe to show verbatim.
function isPrivateOrDocIp(ip: string): boolean {
  const l = ip.toLowerCase();
  // IPv4 private / link-local / loopback
  if (/^10\./.test(l)) return true;
  if (/^192\.168\./.test(l)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(l)) return true;
  if (/^127\./.test(l)) return true;
  if (/^169\.254\./.test(l)) return true;
  // IPv4 documentation ranges (RFC 5737) — the demo's fake public IPs
  if (/^(192\.0\.2|198\.51\.100|203\.0\.113)\./.test(l)) return true;
  // IPv6 loopback / link-local / unique-local
  if (l === "::1") return true;
  if (l.startsWith("fe80:")) return true;
  if (l.startsWith("fc") || l.startsWith("fd")) return true;
  // IPv6 documentation range (RFC 3849)
  if (l.startsWith("2001:db8")) return true;
  return false;
}

// displayLocalIps — demo hygiene for the per-endpoint local-IP list. Enrolled
// devices report their real interface addresses, which can include a real
// public IPv6 global (e.g. an ISP 2600:… prefix). Keep genuinely private
// addresses; replace any real public address with a documentation-range
// placeholder (RFC 5737 / RFC 3849), preserving count and v4/v6 type.
export function displayLocalIps(ips: string[] | undefined | null): string[] {
  if (!ips) return [];
  let v4 = 0;
  let v6 = 0;
  return ips.map((ip) => {
    if (isPrivateOrDocIp(ip)) return ip;
    if (ip.includes(":")) {
      v6 += 1;
      return `2001:db8::${v6}`;
    }
    v4 += 1;
    return `198.51.100.${100 + v4}`;
  });
}

export function displayUsername(name: string | undefined | null): string {
  if (!name) return "";
  return ALIASES[name] ?? name;
}

// displayFullName resolves a friendly "First Last" name for a row.
// Precedence: hard-coded override map → probe-reported full_name →
// derived-from-email (when the identity is itself an email) →
// the (display-mapped) username as last-resort fallback.
export function displayFullName(
  username: string | undefined | null,
  fullName?: string | null,
): string {
  if (username && FULL_NAMES[username]) return FULL_NAMES[username];
  if (fullName && fullName.trim()) return fullName.trim();
  if (isEmail(username)) return titleizeLocalPart(username);
  return displayUsername(username);
}

// displayEmail resolves the best available email for a row. Precedence:
// hard-coded override map → probe-reported email → the identity itself when
// it IS an email (connector-bound rows) → "" (caller renders dash).
export function displayEmail(
  username: string | undefined | null,
  email?: string | null,
): string {
  if (username && EMAIL_OVERRIDES[username]) return EMAIL_OVERRIDES[username];
  if (email && email.trim()) return email.trim();
  if (isEmail(username)) return username;
  return "";
}
